import React from 'react';
import { useCurrentUserPlayer } from '@/hooks/useCurrentUserPlayer';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Hash, GraduationCap, User } from 'lucide-react';

interface CurrentPlayerDetailsProps {
  teamId: string;
  teamName: string;
}

// Export player data and loading state for parent component
// eslint-disable-next-line react-refresh/only-export-components
export const useCurrentPlayerDetailsData = (teamId: string) => {
  const { player, loading } = useCurrentUserPlayer(teamId);
  return { player, loading };
};

export const CurrentPlayerDetails = ({ teamId, teamName }: CurrentPlayerDetailsProps) => {
  const { player, loading } = useCurrentUserPlayer(teamId);

  if (loading) {
    return <p className="text-xs text-muted-foreground">Loading your player data...</p>;
  }

  if (!player) {
    return <p className="text-xs text-muted-foreground">You are not registered as a player on this team</p>;
  }

  const getPlayerInitials = () => {
    const firstName = player.profile?.first_name || '';
    const lastName = player.profile?.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs">
        <Avatar className="h-8 w-8">
          <AvatarImage src={player.profile_picture_url} alt={`${player.profile?.first_name || ''} ${player.profile?.last_name || ''}`.trim()} />
          <AvatarFallback className="text-xs">
            {getPlayerInitials() || <User className="h-3 w-3" />}
          </AvatarFallback>
        </Avatar>
        
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            {player.jersey_number && (
              <div className="flex items-center gap-1">
                <Hash className="w-3 h-3 text-muted-foreground" />
                <span className="text-muted-foreground">{player.jersey_number}</span>
              </div>
            )}
            {player.position && (
              <Badge variant="outline" className="text-xs px-1 py-0 h-auto">
                {player.position}
              </Badge>
            )}
            {player.grad_year && (
              <div className="flex items-center gap-1">
                <GraduationCap className="w-3 h-3 text-muted-foreground" />
                <span className="text-muted-foreground">Class of {player.grad_year}</span>
              </div>
            )}
          </div>
          
        </div>
      </div>
    </div>
  );
};